<script lang="ts">
  // Die Huelle (docs/shell.md).
  //
  // Aufbau, von aussen nach innen:
  //
  //   Aktivitaetsleiste │ Seitenleiste │ Arbeitsflaeche (Tabs)
  //                     │              ├──────────────────────
  //                     │              │ unteres Panel
  //
  // Seitenleiste, Arbeitsflaeche und Panel sind eigene KAESTEN auf einem
  // gemeinsamen Grund, nicht Flaechen mit Trennstrichen dazwischen (Tim
  // 15.08., so macht es das aktuelle VS Code). Der Grund ist --bg, die
  // Kaesten sind --bg-elev: in Hell heisst das weisser Kasten auf grauem
  // Grund, in Dunkel heller Kasten auf dunklerem -- dieselbe Aussage in
  // beiden Themes, ohne eine einzige neue Farbe.
  //
  // Titelleiste, Aktivitaetsleiste und Statusleiste bleiben AUF dem
  // Grund. Sie sind Rahmen, kein Inhalt -- ein Kasten um sie herum
  // wuerde die Ordnung verwischen.
  //   ─────────────────────────────────────────────────────────
  //   Statusleiste (unbunt -- siehe StatusBar.svelte)
  //
  // Sie ist bewusst duenn: sie haelt Layout, Tastatur und die Kopplung
  // an die Adresszeile. Was in einem Tab passiert, geht sie nichts an.

  import { onMount, untrack } from "svelte";

  import { t } from "../i18n.svelte";

  import ActivityBar from "./ActivityBar.svelte";
  import BottomPanel from "./BottomPanel.svelte";
  import CommandPalette from "./CommandPalette.svelte";
  import EditorArea from "./EditorArea.svelte";
  import Explorer from "./Explorer.svelte";
  import Griff from "./Griff.svelte";
  import StatusBar from "./StatusBar.svelte";
  import { sectionForPath } from "../nav-sections";
  import {
    folgeSektion,
    panelOffen,
    schaltePanel,
    schalteSeitenleiste,
    seitenleisteBreite,
    seitenleisteOffen,
    setzeSeitenleisteBreite,
    sichtbareSektion,
    stelleChromeHer,
  } from "./chrome.svelte";
  import { prefsGeladen } from "../prefs.svelte";
  import { merkeSeite, stelleVerlaufHer } from "./recent.svelte";
  import { bootLayout, haltUrlNach, holeLayoutNach, starteRouteSync } from "./route-sync.svelte";
  import {
    aktiverTab,
    schliesseTab,
    shellFokus,
    shellGroups,
    teile,
  } from "./tabs.svelte";

  interface Props {
    /** Unter diesem Wert gibt es keine zwei Spalten. */
    schmal?: boolean;
  }
  let { schmal = false }: Props = $props();

  let paletteOffen = $state(false);

  onMount(() => {
    stelleChromeHer();
    stelleVerlaufHer();
    bootLayout();
    return starteRouteSync();
  });

  // Zweiter Anlauf, sobald der Server-Stand steht.
  //
  // Der Mount oben laeuft aus dem localStorage-Cache. Ist der kalt --
  // neues Geraet, geleerte Website-Daten, privates Fenster, jeder
  // Headless-Lauf von scripts/agent-verify.sh -- findet er NICHTS und
  // die Shell startet nackt, obwohl der gemerkte Arbeitsplatz auf dem
  // Server liegt. Vorher hat der erste Schreiber ihn danach auch noch
  // ueberschrieben; das verhindern die Schreiber jetzt selbst
  // (prefsGeladen()-Sperre). Hier holen wir ihn zurueck.
  //
  // untrack: die drei Aufrufe LESEN und SCHREIBEN dieselben Prefs --
  // ohne das weckt der Effekt sich selbst (dieselbe Falle wie bei
  // merkeSeite und folgeSektion, docs/shell.md).
  $effect(() => {
    if (!prefsGeladen()) return;
    untrack(() => {
      stelleChromeHer();
      stelleVerlaufHer();
      holeLayoutNach();
    });
  });

  const leiste = $derived(seitenleisteOffen() && !schmal);
  const panel = $derived(panelOffen());
  const sektion = $derived(sichtbareSektion());
  const breite = $derived(seitenleisteBreite());

  // Die neue Breite ist die Zeigerposition minus die linke Kante der
  // Leiste. Diese Kante wird GEMESSEN und nicht gerechnet: sie haengt an
  // der Breite der Aktivitaetsleiste und der Polsterung, und beide sind
  // in rem angegeben -- eine hartkodierte Pixelzahl waere schon beim
  // Wechsel der Basis-Schriftgroesse falsch (45px statt 48px, 15.08.).
  let mitteEl = $state<HTMLElement | null>(null);
  function zieheLeiste(pos: { x: number; y: number }) {
    const leisteEl = mitteEl?.querySelector(".explorer");
    if (!leisteEl) return;
    setzeSeitenleisteBreite(pos.x - leisteEl.getBoundingClientRect().left);
  }

  // Der fokussierte Tab bestimmt die Adresszeile. Als Effekt, damit
  // JEDER Weg dorthin (Klick, Tastatur, Schliessen eines Tabs) sie
  // mitzieht -- nicht nur die, an die wir beim Bauen gedacht haben.
  $effect(() => {
    const tab = aktiverTab();
    haltUrlNach();
    // Derselbe Moment ist auch "diese Seite wurde aufgerufen" -- der
    // Verlauf haengt damit an genau einer Stelle statt an jedem
    // einzelnen Aufrufer (Baum, Palette, Link, Zurueck-Taste).
    if (tab?.typ === "page") merkeSeite(String(tab.params.path ?? ""));

    // Die Seitenleiste folgt dem Tab: wer eine Seite oeffnet, sieht
    // daneben deren Bereich -- vorher konnte man in einem System-Tab
    // arbeiten, waehrend links Sicherheit stand (Tim 18.08.).
    //
    // Der Effekt haengt an der Tab-IDENTITAET, nicht an der Sektion.
    // Damit ueberlebt ein Klick in der Aktivitaetsleiste (bewusst in
    // einen anderen Bereich geschaut) genau so lange, bis man den Tab
    // wechselt -- und der Effekt startet sich nicht selbst neu, weil
    // er die Sektion nie LIEST (lesson_svelte_effect_write_read_cycle).
    if (tab?.typ === "page") {
      const sektion = sectionForPath(String(tab.params.path ?? "/"));
      // untrack, weil folgeSektion die Sektion LIEST (Vergleich vor dem
      // Schreiben). Ohne das abonniert der Effekt sie -- und ein Klick
      // in der Aktivitaetsleiste startete ihn neu, worauf er die
      // Auswahl sofort wieder ueberschrieb. Gemessen 18.08.: der Klick
      // auf "Sicherheit" blieb wirkungslos.
      if (sektion) untrack(() => folgeSektion(sektion.id));
    }
  });

  function beiTaste(e: KeyboardEvent) {
    const mod = e.metaKey || e.ctrlKey;
    if (!mod) return;

    const taste = e.key.toLowerCase();

    // Suchen und springen. Strg+P wie in VS Code; der Browser-Druckdialog
    // (Strg+P) wird dabei bewusst uebernommen -- in einer Oberflaeche
    // ohne Druckseiten ist das die nuetzlichere Belegung.
    if (taste === "p") {
      e.preventDefault();
      paletteOffen = true;
      return;
    }
    // Seitenleiste / unteres Panel
    if (taste === "b") {
      e.preventDefault();
      schalteSeitenleiste();
      return;
    }
    if (taste === "j") {
      e.preventDefault();
      schaltePanel();
      return;
    }
    // Editor teilen.
    if (e.key === "\\") {
      e.preventDefault();
      teile();
      return;
    }
    // Aktiven Tab schliessen (Strg+W faengt der Browser ab, daher
    // Strg+Shift+W wie fuer Fenster in VS Code).
    if (e.shiftKey && taste === "w") {
      e.preventDefault();
      const tab = aktiverTab();
      if (tab) schliesseTab(shellFokus(), tab.id);
      return;
    }
    // Zwischen den Tabs der fokussierten Spalte blaettern.
    if (e.key === "PageDown" || e.key === "PageUp") {
      const gruppen = shellGroups();
      const g = gruppen[shellFokus()];
      if (!g || g.tabs.length < 2) return;
      e.preventDefault();
      const idx = g.tabs.findIndex((t) => t.id === g.activeId);
      const naechster =
        e.key === "PageDown"
          ? (idx + 1) % g.tabs.length
          : (idx - 1 + g.tabs.length) % g.tabs.length;
      g.activeId = g.tabs[naechster].id;
    }
  }
</script>

<svelte:window on:keydown={beiTaste} />

<div class="shell" style="height: var(--app-vh, 100dvh);">
  <div class="mitte flex min-h-0 flex-1" bind:this={mitteEl}>
    <!-- Leiste und Baum in einem Kasten (app.css „seiten-rahmen") -->
    <div class="seiten-rahmen" class:mit-baum={leiste}>
      <ActivityBar onpalette={() => (paletteOffen = true)} />
      {#if leiste}<Explorer {sektion} {breite} />{/if}
    </div>
    {#if leiste}
      <Griff
        achse="x"
        label={t("shell.sidebar_width", "Breite der Seitenleiste ändern")}
        wert={breite}
        min={160}
        max={560}
        onzug={zieheLeiste}
        onschritt={(d) => setzeSeitenleisteBreite(breite + d)}
      />
    {/if}
    <div class="flex min-h-0 min-w-0 flex-1 flex-col">
      <EditorArea {schmal} />
      {#if panel}
        <BottomPanel />
      {/if}
    </div>
  </div>
  <StatusBar />
</div>

{#if paletteOffen}
  <CommandPalette onclose={() => (paletteOffen = false)} />
{/if}

<style>
  .shell {
    display: flex;
    flex-direction: column;
    overflow: hidden;
    background: var(--shell-grund, var(--bg));
  }
  /* KEIN gap hier. Zwischen Seitenleiste und Arbeitsflaeche liegt der
     Griff, und der ist selbst 6px breit -- mit einem gap daneben kaeme
     dreimal derselbe Abstand zusammen (6 + 6 + 6 = 18px), waehrend
     zwischen Editor und Panel nur die 6px des Griffs liegen. Tim
     15.08.: "der raum zwischen tree und editor ist viel zu gross".
     Leiste und Baum sind seit 18.10.2026 ein Kasten (app.css
     „seiten-rahmen"); ohne Baum traegt er selbst den Abstand. */
  .mitte {
    /* Rundherum dasselbe Mass. Die 0 oben stammte aus der Zeit, als
       die obere Leiste den Abstand stellte -- ohne sie klebten die
       Kaesten am Fensterrand (Tim 18.08.). */
    padding: 6px;
  }
</style>
